import { useMemo } from "react";
import { dataVizColors } from "../common/colors";
import { roundToNiceMaxValue } from "./utils";

interface PrepareLineDataParams {
  _data: any[]; // Input data array
  xAxisPath?: string; // Optional path to the x-axis value (auto-detected if not provided)
  labelPath?: string; // Optional path to the label/name
  valuePath?: string | string[]; // Optional path to value(s) to display (auto-detected if not provided)
  tooltipLabelsMapping?: { [key: string]: string }; // Optional mapping for tooltip labels
  colorIdx?: number; // Starting color index
  colors?: { [key: string]: string }; // Optional per-line color mapping { valuePath: "#hex" }
}

interface LineConfig {
  dataKey: string;
  name: string;
  color: string;
  id: string;
}

// This type matches what the CustomTooltip component expects
export interface TooltipLabel {
  count: number;
  name: string;
  tooltipLabel: string;
  barColor: string;
  textColor: string;
  percentage?: number;
}

interface PreparedLineData {
  data: any[]; // Transformed data ready for LineChart
  dataMaxValue: number; // Maximum value for domain calculation
  lines: LineConfig[]; // Configuration for each line
  xAxis: string; // The x-axis field name that was used (detected or provided)
  tooltipData: TooltipLabel[][]; // Data prepared for the CustomTooltip component
}

// Function to get line color based on index
const getLineColor = (colorIdx: number, index: number): string => {
  return dataVizColors[(colorIdx + index) % dataVizColors.length];
};

// Utility to check if a value is a number or can be converted to a number
const isNumeric = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  return !isNaN(parseFloat(value)) && isFinite(value);
};

// Function to auto-detect the data structure and extract relevant fields
const detectDataStructure = (
  data: any[], 
  providedXAxis?: string,
  providedValuePaths?: string | string[]
): {
  xAxis: string;
  valuePaths: string[];
  apiFormat: boolean;
} => {
  if (data.length === 0) {
    return { xAxis: 'x', valuePaths: [], apiFormat: false };
  }

  // If paths are provided, use them
  if (providedXAxis && providedValuePaths) {
    return {
      xAxis: providedXAxis,
      valuePaths: Array.isArray(providedValuePaths) ? providedValuePaths : [providedValuePaths],
      apiFormat: false
    };
  }

  // Check if data follows the API format (with dimensions array)
  const firstItem = data[0];
  if (firstItem.dimensions && Array.isArray(firstItem.dimensions)) {
    // API format detected
    return {
      xAxis: providedXAxis || 'timeInterval',
      valuePaths: ['dimensions'],
      apiFormat: true
    };
  }

  // Auto-detect fields
  const sampleFields = Object.keys(firstItem);
  
  // Find a suitable x-axis field
  let xAxis = providedXAxis;
  if (!xAxis) {
    // If no xAxisPath provided, use the first non-numeric field
    // or the first field if all are numeric
    const nonNumericFields = sampleFields.filter(field => !isNumeric(firstItem[field]));
    
    if (nonNumericFields.length > 0) {
      xAxis = nonNumericFields[0];
    } else {
      xAxis = sampleFields[0];
    }
  }

  // Find value fields if not provided
  let valuePaths: string[] = [];
  if (!providedValuePaths) {
    // Use all numeric fields except the x-axis field
    valuePaths = sampleFields.filter(field => 
      field !== xAxis && isNumeric(firstItem[field])
    );
  } else {
    // Use provided value paths
    valuePaths = Array.isArray(providedValuePaths) ? providedValuePaths : [providedValuePaths];
  }

  return { xAxis, valuePaths, apiFormat: false };
};

export const usePrepareLineData = ({
  _data,
  xAxisPath,
  labelPath,
  valuePath,
  tooltipLabelsMapping,
  colorIdx = 0,
  colors: colorOverrides,
}: PrepareLineDataParams): PreparedLineData => {
  
  return useMemo(() => {
    // Handle empty data case
    if (!_data || _data.length === 0) {
      return { 
        data: [], 
        dataMaxValue: 0, 
        lines: [], 
        xAxis: "", 
        tooltipData: [],
      };
    }

    // Auto-detect data structure
    const { xAxis, valuePaths, apiFormat } = detectDataStructure(_data, xAxisPath, valuePath);
    
    // Configure lines for the chart
    const lines: LineConfig[] = [];
    let transformedData: any[] = [];
    let tooltipData: TooltipLabel[][] = [];
    
    if (apiFormat) {
      // Handle the API format with dimensions array
      const uniqueDimensions = new Map<string, { value: string, display: string }>();
      
      // Find all unique dimensions across all time intervals
      _data.forEach(item => {
        if (item.dimensions && Array.isArray(item.dimensions)) {
          item.dimensions.forEach(dimension => {
            if (dimension.value && !uniqueDimensions.has(dimension.value)) {
              uniqueDimensions.set(dimension.value, {
                value: dimension.value,
                display: dimension.display || dimension.value
              });
            }
          });
        }
      });
      
      // Create lines for each dimension. Honor `colors` overrides (keyed by the
      // dimension's stable value/id, with display as a loose fallback) so callers
      // can colour lines by meaning (e.g. signal category); else the auto palette.
      Array.from(uniqueDimensions.values()).forEach((dimension, index) => {
        const color =
          colorOverrides?.[dimension.value] ||
          colorOverrides?.[dimension.display] ||
          getLineColor(colorIdx, index);
        lines.push({
          dataKey: `${dimension.value}_count`,
          name: dimension.display || dimension.value,
          color,
          id: `line-${index}`
        });
      });
      
      // Transform data to flat format with one field per dimension
      transformedData = _data.map(item => {
        const transformedItem: any = {
          [xAxis]: item[xAxis],
        };
        
        // Initialize all dimension counts to 0
        uniqueDimensions.forEach(dimension => {
          transformedItem[`${dimension.value}_count`] = 0;
        });
        
        // Set the actual counts from dimensions
        if (item.dimensions && Array.isArray(item.dimensions)) {
          item.dimensions.forEach(dimension => {
            if (dimension.value && transformedItem[`${dimension.value}_count`] !== undefined) {
              transformedItem[`${dimension.value}_count`] = dimension.count || 0;
            }
          });
        }
        
        return transformedItem;
      });
      
      // Prepare tooltip data for each data point
      // Use the full set of unique dimensions so that every line appears in the tooltip, even when a particular time interval doesn't have data for all dimensions.
      const allDimensionEntries = Array.from(uniqueDimensions.entries());
      tooltipData = _data.map(item => {
        const dimensionCounts = new Map<string, { count: number; display: string }>();
        if (item.dimensions && Array.isArray(item.dimensions)) {
          item.dimensions.forEach(dimension => {
            if (dimension.value) {
              dimensionCounts.set(dimension.value, {
                count: dimension.count || 0,
                display: dimension.display || dimension.value,
              });
            }
          });
        }

        const entries = allDimensionEntries.map(([key, dim], index) => {
          const found = dimensionCounts.get(key);
          return {
            count: found ? found.count : 0,
            name: key,
            tooltipLabel: found ? found.display : dim.display,
            barColor: colorOverrides?.[key] || colorOverrides?.[dim.display] || getLineColor(colorIdx, index),
            textColor: "#FFFFFF",
          };
        });
        entries.sort((a, b) => b.count - a.count);
        return entries;
      });
      
    } else {
      // Handle regular flat data format
      transformedData = [..._data];
      
      // Create a line for each value path
      valuePaths.forEach((path, index) => {
        const name = tooltipLabelsMapping && tooltipLabelsMapping[path]
          ? tooltipLabelsMapping[path]
          : path;
        const color = colorOverrides?.[path] || getLineColor(colorIdx, index);

        lines.push({
          dataKey: path,
          name: name,
          color: color,
          id: `line-${index}`
        });
      });

      // Prepare tooltip data for each data point
      tooltipData = transformedData.map(item => {
        return valuePaths.map((path, index) => {
          const value = item[path] || 0;
          const name = tooltipLabelsMapping && tooltipLabelsMapping[path]
            ? tooltipLabelsMapping[path]
            : path;
          const color = colorOverrides?.[path] || getLineColor(colorIdx, index);
          
          return {
            count: value,
            name: path,
            tooltipLabel: name,
            barColor: color,
            textColor: "#FFFFFF",
          };
        });
      });
    }

    // Calculate maximum value for scaling
    let dataMaxValue = 0;
    
    // Find max across all lines
    dataMaxValue = Math.max(
      ...transformedData.flatMap(item => 
        lines.map(line => item[line.dataKey] || 0)
      ),
      1 // Ensure we have a non-zero value even if all data is 0
    );
    
    // Add a small buffer (5%) to the max value for better visualization
    // dataMaxValue *= 1.05;
    
    // Round up to a nice value using the shared utility
    dataMaxValue = roundToNiceMaxValue(dataMaxValue);

    return {
      data: transformedData,
      dataMaxValue,
      lines,
      xAxis,
      tooltipData,
    };
  }, [_data, xAxisPath, labelPath, valuePath, tooltipLabelsMapping, colorIdx]);
};